import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  Text,
  VStack,
  HStack,
  Progress,
  Badge,
  Tooltip,
  IconButton,
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
} from "@chakra-ui/react";
import { useGame } from "../../contexts/GameContext";
import { JeopardyStates, EventTypes } from "types";
import { TextFit } from "../TextFit";
import { every } from "lodash";
import { FaCheck, FaTimes } from "react-icons/fa";

export interface HostOnlyControlsProps {
  hostBar: React.ReactNode;
}

export const HostOnlyControls: React.FC<HostOnlyControlsProps> = ({
  hostBar,
}) => {
  const { gameState, sendEvent, activeCategory } = useGame();
  const [vettingInProgress, setVettingInProgress] = useState<string | null>(null);

  // Get categories pending vetting (for host)
  const pendingVettingCategories = useMemo(() => {
    const submittedCategories = gameState?.submittedCategories || {};
    return Object.entries(submittedCategories)
      .filter(([, data]) => data.vetted !== true)
      .map(([categoryId, data]) => ({
        categoryId,
        topic: data.topic,
        submitterName: data.submitterName,
      }));
  }, [gameState?.submittedCategories]);

  // Check if all players' audio is ready (or if TTS is disabled)
  const playerAudioStatus = useMemo(() => {
    const players = gameState?.players || [];
    const audioProgress = gameState?.playerAudioProgress || {};
    const enableTTS = gameState?.enableTTS !== false;

    if (!enableTTS || players.length === 0) {
      return { allReady: true, generating: 0, complete: 0, total: 0 };
    }

    let generating = 0;
    let complete = 0;

    players.forEach((player) => {
      const progress = audioProgress[player.name];
      if (progress?.status === "complete") {
        complete++;
      } else if (progress?.status === "generating") {
        generating++;
      }
    });

    return {
      allReady: complete === players.length,
      generating,
      complete,
      total: players.length,
    };
  }, [gameState?.players, gameState?.playerAudioProgress, gameState?.enableTTS]);

  // Check if game clue audio for Jeopardy round is ready
  const gameAudioStatus = useMemo(() => {
    const enableTTS = gameState?.enableTTS !== false;
    const ttsProgress = gameState?.ttsProgress;

    if (!enableTTS) {
      return { jeopardyReady: true, current: 0, total: 0, message: "" };
    }

    return {
      jeopardyReady: ttsProgress?.jeopardyRoundComplete ?? false,
      doubleJeopardyReady: ttsProgress?.doubleJeopardyRoundComplete ?? false,
      current: ttsProgress?.current ?? 0,
      total: ttsProgress?.total ?? 0,
      message: ttsProgress?.message ?? "",
    };
  }, [gameState?.enableTTS, gameState?.ttsProgress]);

  // Game is ready when player audio AND Jeopardy round clue audio are complete
  const isGameReady = playerAudioStatus.allReady && gameAudioStatus.jeopardyReady;

  // Get detailed progress for display
  const playerProgressList = useMemo(() => {
    const players = gameState?.players || [];
    const audioProgress = gameState?.playerAudioProgress || {};

    return players.map((player) => {
      const progress = audioProgress[player.name];
      return {
        name: player.name,
        current: progress?.current || 0,
        total: progress?.total || 9,
        status: progress?.status || "pending",
      };
    });
  }, [gameState?.players, gameState?.playerAudioProgress]);

  const onStartGame = async () => {
    sendEvent({ type: EventTypes.START_GAME });
  };

  const onClueSelect = ({
    clueId,
    categoryId,
  }: {
    clueId: string;
    categoryId: string;
  }) => {
    sendEvent({ type: EventTypes.SELECT_CATEGORY, clueId, categoryId });
  };

  const onVetCategory = async (categoryId: string) => {
    if (vettingInProgress) return;

    setVettingInProgress(categoryId);
    try {
      await sendEvent({
        type: EventTypes.VET_CATEGORY,
        categoryId,
      });
    } catch (error) {
      console.error("[HostOnlyControls] Error vetting category:", error);
    } finally {
      setVettingInProgress(null);
    }
  };

  const onRejectCategory = async (categoryId: string) => {
    if (vettingInProgress) return;

    setVettingInProgress(categoryId);
    try {
      await sendEvent({
        type: EventTypes.REJECT_CATEGORY,
        categoryId,
      });
    } catch (error) {
      console.error("[HostOnlyControls] Error rejecting category:", error);
    } finally {
      setVettingInProgress(null);
    }
  };

  if (!gameState) {
    return null;
  }

  const { categories } = gameState;

  // Find the last answered clue by ID to show the answer
  const lastAnsweredClue = gameState.lastAnsweredClueId
    ? categories
        .flatMap((c) => c.clues)
        .find((clue) => clue.id === gameState.lastAnsweredClueId)
    : null;

  const renderContent = () => {
    switch (gameState.currentState) {
      case JeopardyStates.WaitingForPlayers:
        return (
          <Center height="100%" width="100%" flexDir="column" gap="20px" p={4}>
            <Flex
              flexDirection="column"
              gap="16px"
              width="90%"
              maxWidth="400px"
              alignItems="center"
            >
              <Text textAlign="center" textStyle="clue">
                Host Controls - Start game when ready
              </Text>

              {/* Players joined */}
              <Box
                width="100%"
                bg="rgba(0,0,0,0.3)"
                p={4}
                borderRadius="lg"
              >
                <Text textStyle="clue" fontSize="sm" mb={2}>
                  Players ({gameState.players.length})
                </Text>
                {gameState.players.length === 0 ? (
                  <Text textStyle="clue" fontSize="sm" opacity={0.7}>
                    Waiting for players to join...
                  </Text>
                ) : (
                  <VStack spacing={1} align="stretch">
                    {gameState.players.map((player) => (
                      <HStack key={player.name} justify="space-between">
                        <Text textStyle="clue" fontSize="sm">
                          {player.name}
                        </Text>
                        <Text textStyle="clue" fontSize="sm" opacity={0.7}>
                          ${player.score}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Box>

              {/* Audio generation progress */}
              {!isGameReady && gameState.players.length > 0 && (
                <Box
                  width="100%"
                  bg="rgba(0,0,0,0.4)"
                  p={4}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                >
                  <Text textStyle="clue" fontSize="sm" mb={3} textAlign="center">
                    Generating Voice Audio
                  </Text>
                  <VStack spacing={3} width="100%">
                    {playerAudioStatus.total > 0 &&
                      playerProgressList.map((player) => (
                        <Box key={player.name} width="100%">
                          <HStack justify="space-between" mb={1}>
                            <Text textStyle="clue" fontSize="xs">
                              {player.name}
                            </Text>
                            <Text textStyle="clue" fontSize="xs" opacity={0.7}>
                              {player.status === "complete"
                                ? "✓"
                                : `${player.current}/${player.total}`}
                            </Text>
                          </HStack>
                          <Progress
                            value={(player.current / player.total) * 100}
                            size="sm"
                            colorScheme={
                              player.status === "complete" ? "green" : "yellow"
                            }
                            bg="whiteAlpha.200"
                            borderRadius="full"
                            hasStripe={player.status === "generating"}
                            isAnimated={player.status === "generating"}
                          />
                        </Box>
                      ))}

                    {gameAudioStatus.total > 0 && (
                      <Box width="100%">
                        <HStack justify="space-between" mb={1}>
                          <Text textStyle="clue" fontSize="xs">
                            Clue Audio
                          </Text>
                          <Text textStyle="clue" fontSize="xs" opacity={0.7}>
                            {gameAudioStatus.jeopardyReady
                              ? "✓ Round 1 Ready"
                              : gameAudioStatus.message ||
                                `${gameAudioStatus.current}/${gameAudioStatus.total}`}
                          </Text>
                        </HStack>
                        <Progress
                          value={
                            (gameAudioStatus.current / gameAudioStatus.total) *
                            100
                          }
                          size="sm"
                          colorScheme={
                            gameAudioStatus.jeopardyReady ? "green" : "blue"
                          }
                          bg="whiteAlpha.200"
                          borderRadius="full"
                          hasStripe={!gameAudioStatus.jeopardyReady}
                          isAnimated={!gameAudioStatus.jeopardyReady}
                        />
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}

              {/* All ready indicator */}
              {isGameReady && gameState.players.length > 0 && (
                <Box
                  width="100%"
                  bg="green.600"
                  p={3}
                  borderRadius="md"
                  textAlign="center"
                >
                  <Text textStyle="clue" fontSize="sm">
                    ✓ Ready to play!
                  </Text>
                </Box>
              )}

              <Button
                onClick={onStartGame}
                isDisabled={!isGameReady || gameState.players.length === 0}
                width="100%"
                size="lg"
                colorScheme={isGameReady && gameState.players.length > 0 ? "yellow" : "gray"}
              >
                {gameState.players.length === 0
                  ? "Waiting for players..."
                  : isGameReady
                  ? "Start Game"
                  : "Waiting for audio..."}
              </Button>

              {/* Category Vetting Section */}
              {pendingVettingCategories.length > 0 && (
                <Box
                  width="100%"
                  bg="rgba(0,0,0,0.4)"
                  p={4}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="orange.400"
                >
                  <HStack justify="space-between" mb={3}>
                    <Text textStyle="clue" fontSize="sm" color="orange.300">
                      Categories Pending Approval
                    </Text>
                    <Badge colorScheme="orange">
                      {pendingVettingCategories.length} pending
                    </Badge>
                  </HStack>
                  <VStack spacing={2} width="100%">
                    {pendingVettingCategories.map(
                      ({ categoryId, topic, submitterName }) => (
                        <Box
                          key={categoryId}
                          width="100%"
                          p={3}
                          borderRadius="md"
                          bg="whiteAlpha.100"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                        >
                          <HStack justify="space-between" align="center">
                            <VStack align="start" spacing={0} flex={1}>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color="white"
                              >
                                {topic}
                              </Text>
                              <Text fontSize="xs" color="whiteAlpha.600">
                                by {submitterName}
                              </Text>
                            </VStack>
                            <HStack spacing={2}>
                              <Tooltip label="Approve for voting">
                                <IconButton
                                  aria-label="Approve category"
                                  icon={<FaCheck />}
                                  size="sm"
                                  colorScheme="green"
                                  onClick={() => onVetCategory(categoryId)}
                                  isLoading={vettingInProgress === categoryId}
                                />
                              </Tooltip>
                              <Tooltip label="Reject category">
                                <IconButton
                                  aria-label="Reject category"
                                  icon={<FaTimes />}
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => onRejectCategory(categoryId)}
                                  isLoading={vettingInProgress === categoryId}
                                />
                              </Tooltip>
                            </HStack>
                          </HStack>
                        </Box>
                      )
                    )}
                  </VStack>
                </Box>
              )}
            </Flex>
          </Center>
        );

      case JeopardyStates.CategorySelection:
        return (
          <Flex flexDir="column" height="100%" width="100%">
            {/* Host admin header */}
            <Box
              bg="rgba(0,0,0,0.4)"
              px={4}
              py={2}
              borderBottom="1px solid"
              borderColor="whiteAlpha.200"
            >
              <HStack justify="space-between" align="center">
                <HStack spacing={2}>
                  <Badge colorScheme="purple" fontSize="xs">
                    HOST CONTROLS
                  </Badge>
                  <Text textStyle="clue" fontSize="sm">
                    Select clue for{" "}
                    <Text as="span" color="jeopardy.gold" fontWeight="bold">
                      {gameState.activePlayer?.name || "player"}
                    </Text>
                  </Text>
                </HStack>
                {lastAnsweredClue && (
                  <Text textStyle="clue" fontSize="xs" opacity={0.7}>
                    Last: {lastAnsweredClue.answer}
                  </Text>
                )}
              </HStack>
            </Box>

            <Accordion
              display="flex"
              flexDirection="column"
              justifyContent="center"
              flex={1}
              width="100%"
              allowToggle
            >
              {categories
                .filter((c) => c.roundType === gameState.currentRound)
                .map((category) => (
                  <AccordionItem
                    flexGrow={1}
                    display="flex"
                    flexDir="column"
                    justifyContent="center"
                    key={category.id}
                    isDisabled={every(category.clues, (clue) => clue.answered)}
                  >
                    <AccordionButton flexGrow={1}>
                      <TextFit
                        max={3}
                        style={{ textWrap: "nowrap" }}
                        as={Flex}
                        justifyContent="flex-start"
                        alignItems="center"
                        textStyle="category"
                      >
                        {category.name}
                      </TextFit>
                    </AccordionButton>
                    <AccordionPanel py="2em">
                      <Flex gap="3px" justifyContent="space-around">
                        {category.clues.map((clue) => (
                          <Button
                            height="0"
                            width="calc(100% / 6)"
                            pb="calc(100% / 6)"
                            borderColor="jeopardy.gold"
                            borderWidth="2px"
                            borderStyle="solid"
                            background="jeopardy.blue"
                            isDisabled={clue.answered}
                            key={clue.id}
                            onClick={() =>
                              onClueSelect({
                                clueId: clue.id,
                                categoryId: category.id,
                              })
                            }
                            position="relative"
                          >
                            <Center
                              width="100%"
                              position="absolute"
                              top={0}
                              right={0}
                              bottom={0}
                            >
                              <Text left={0} textStyle="value" fontSize="1.5em">
                                ${clue.value}
                              </Text>
                            </Center>
                          </Button>
                        ))}
                      </Flex>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
            </Accordion>
          </Flex>
        );

      case JeopardyStates.WaitingForBuzz:
        return (
          <VStack spacing={4} p={4}>
            {activeCategory && (
              <TextFit
                width="80%"
                height="auto"
                textAlign="center"
                as={Center}
                textStyle="category"
              >
                {activeCategory.name}
              </TextFit>
            )}
            <Text textStyle="clue" fontSize="xl">
              Waiting for players to buzz in...
            </Text>
            {gameState.activeClue && (
              <Box
                bg="jeopardy.clueBlue"
                p={4}
                borderRadius="lg"
                maxWidth="90%"
              >
                <Text textStyle="clue" textAlign="center">
                  ${gameState.activeClue.value}
                </Text>
              </Box>
            )}
          </VStack>
        );

      case JeopardyStates.PlayerAnswering:
        return (
          <VStack spacing={4} p={4}>
            {activeCategory && (
              <TextFit
                width="80%"
                height="auto"
                textAlign="center"
                as={Center}
                textStyle="category"
              >
                {activeCategory.name}
              </TextFit>
            )}
            <Text textStyle="clue" fontSize="xl">
              {gameState.buzzedInPlayer?.name} is answering...
            </Text>
            {gameState.activeClue && (
              <Box
                bg="jeopardy.clueBlue"
                p={4}
                borderRadius="lg"
                maxWidth="90%"
              >
                <Text textStyle="clue" textAlign="center">
                  ${gameState.activeClue.value}
                </Text>
              </Box>
            )}
          </VStack>
        );

      case JeopardyStates.DailyDoubleWager:
        return (
          <VStack spacing={4} p={4}>
            <Text textStyle="clue" fontSize="2xl" color="jeopardy.gold">
              DAILY DOUBLE!
            </Text>
            <Text textStyle="clue" fontSize="xl">
              {gameState.activePlayer?.name} is making their wager...
            </Text>
          </VStack>
        );

      case JeopardyStates.ChallengeVoting:
        return (
          <VStack spacing={4} p={4}>
            <Text textStyle="clue" fontSize="xl">
              Challenge Vote in Progress
            </Text>
            <Text textStyle="clue" opacity={0.7}>
              Players are voting on the challenge...
            </Text>
          </VStack>
        );

      case JeopardyStates.CollectFinalJeopardyWagers:
        return (
          <VStack spacing={4} p={4}>
            <Text textStyle="clue" fontSize="2xl" color="jeopardy.gold">
              FINAL JEOPARDY
            </Text>
            <Text textStyle="clue" fontSize="xl">
              Players are making their wagers...
            </Text>
          </VStack>
        );

      case JeopardyStates.MakeFinalJeopardyGuesses:
        return (
          <VStack spacing={4} p={4}>
            <Text textStyle="clue" fontSize="2xl" color="jeopardy.gold">
              FINAL JEOPARDY
            </Text>
            <Text textStyle="clue" fontSize="xl">
              Players are writing their answers...
            </Text>
          </VStack>
        );

      case JeopardyStates.RevealFinalJeopardyAnswer:
      case JeopardyStates.GameOver:
        return (
          <VStack spacing={4} p={4}>
            <Text textStyle="clue" fontSize="2xl">
              Game Over
            </Text>
            <Box
              width="100%"
              maxWidth="400px"
              bg="rgba(0,0,0,0.3)"
              p={4}
              borderRadius="lg"
            >
              <Text textStyle="clue" fontSize="sm" mb={2}>
                Final Scores
              </Text>
              <VStack spacing={2} align="stretch">
                {[...gameState.players]
                  .sort((a, b) => b.score - a.score)
                  .map((player, index) => (
                    <HStack key={player.name} justify="space-between">
                      <HStack>
                        <Text
                          textStyle="clue"
                          color={index === 0 ? "jeopardy.gold" : "white"}
                          fontWeight={index === 0 ? "bold" : "normal"}
                        >
                          {index + 1}. {player.name}
                        </Text>
                        {index === 0 && (
                          <Badge colorScheme="yellow">Winner!</Badge>
                        )}
                      </HStack>
                      <Text
                        textStyle="clue"
                        color={index === 0 ? "jeopardy.gold" : "white"}
                      >
                        ${player.score}
                      </Text>
                    </HStack>
                  ))}
              </VStack>
            </Box>
          </VStack>
        );

      default:
        return (
          <VStack spacing={4} p={4}>
            <Text textStyle="clue">Game in progress...</Text>
            <Text textStyle="clue" opacity={0.7} fontSize="sm">
              State: {gameState.currentState}
            </Text>
          </VStack>
        );
    }
  };

  return (
    <Flex
      height="100%"
      width="100%"
      flexDir="column"
      background="jeopardy.blue"
    >
      {hostBar}
      <Center flex={1} overflow="auto">
        {renderContent()}
      </Center>
    </Flex>
  );
};
